import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { finalize } from 'rxjs';
import { BoardColumn, BoardColumnColor, BoardColumnType } from '../../board-v3.types';
import { BoardService } from '../../../../services/board.service';
import { ModalService } from '../../../../services/modal.service';
import { DialogService } from '../../../../services/dialog.service';
import { BoardColumnEditComponent } from '../../../../modals/board-column-edit/board-column-edit.component';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../../../services/notifications.service';
import { getApiError } from '../../../warehouse/warehouse.utils';

interface BoardColumnEditResult {
  updatedColumn?: {
    name?: string | null;
    bg_color?: string | null;
    text_color?: string | null;
    type?: BoardColumnType | null;
  };
}

@Component({
  selector: 'app-board-v3-stage-settings',
  templateUrl: './stage-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StageSettingsComponent implements OnChanges {
  @Input() funnelId: string | null = null;
  @Input() columns: BoardColumn[] = [];

  @Output() close = new EventEmitter<{ changed: boolean } | void>();

  private readonly boardService = inject(BoardService);
  private readonly modalService = inject(ModalService);
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  stageColumns: BoardColumn[] = [];
  isSaving = false;
  private changed = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.stageColumns = this.copyColumns(this.columns);
    }
  }

  onClose(): void {
    if (!this.isSaving) this.close.emit(this.changed ? { changed: true } : undefined);
  }

  onStageDrop(event: CdkDragDrop<BoardColumn[]>): void {
    if (!this.funnelId || this.isSaving || event.previousIndex === event.currentIndex) return;

    const previousColumns = this.copyColumns(this.stageColumns);
    const nextColumns = this.copyColumns(this.stageColumns);
    moveItemInArray(nextColumns, event.previousIndex, event.currentIndex);

    if (!this.canApplyColumnOrder(previousColumns, nextColumns)) {
      this.stageColumns = previousColumns;
      return;
    }

    this.stageColumns = nextColumns.map((column, index) => ({ ...column, position: index + 1 }));
    this.isSaving = true;

    this.boardService
      .reorderColumns({
        funnel_id: this.funnelId,
        columns: this.stageColumns.map((column) => ({ id: column.id, position: column.position })),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: (columns) => {
          this.changed = true;
          this.stageColumns = Array.isArray(columns) && columns.length ? this.copyColumns(columns) : this.stageColumns;
        },
        error: () => (this.stageColumns = previousColumns),
      });
  }

  onAddStage(): void {
    this.openStageEditor();
  }

  onEditStage(column: BoardColumn): void {
    this.openStageEditor(column);
  }

  onDeleteStage(column: BoardColumn): void {
    if (this.isSaving || this.isLastSystemColumn(column)) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_delete_stage_confirm', { name: column.name }),
        isTranslate: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;

        this.isSaving = true;
        this.boardService
          .deleteColumn(column.id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.isSaving = false)),
          )
          .subscribe({
            next: () => {
              this.changed = true;
              this.reloadColumns();
            },
            error: (error: unknown) => {
              // The reserve / write-off column of the funnel warehouse settings can not be deleted
              if (getApiError(error).code !== 'COLUMN_USED_IN_WAREHOUSE_SETTINGS') return;
              this.notificationService.showError(
                '',
                this.translateService.instant('board_v3_stage_used_in_warehouse_settings'),
              );
            },
          });
      });
  }

  private openStageEditor(column?: BoardColumn): void {
    if (!this.funnelId || this.isSaving) return;

    const initialColor: BoardColumnColor = column?.color ?? { background: '#f6f8f9', text: '#000000' };
    this.modalService
      .openModal({
        title: column ? 'change_column' : 'create_column',
        translate: true,
        component: BoardColumnEditComponent,
        width: '420px',
        data: {
          column: {
            name: column?.name ?? '',
            color: initialColor,
            type: column?.type ?? 'DEFAULT',
          },
          canEditType: !column,
          excludedColumnTypes: column ? [] : this.getExcludedColumnTypesForCreate(),
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: BoardColumnEditResult | undefined) => {
        const updatedColumn = result?.updatedColumn;
        const name = updatedColumn?.name?.trim();
        if (!name || !updatedColumn?.bg_color || !updatedColumn.text_color || !this.funnelId) return;

        const color: BoardColumnColor = {
          background: updatedColumn.bg_color,
          text: updatedColumn.text_color,
        };
        const type = updatedColumn.type ?? 'DEFAULT';
        if (!column && type === 'SUCCESS' && this.hasSuccessColumn()) return;

        this.isSaving = true;
        const request = column
          ? this.boardService.updateColumn(column.id, { name, color })
          : this.boardService.createColumn({
              funnel_id: this.funnelId,
              name,
              color,
              type,
              position: this.getNewColumnPosition(type),
            });

        request
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.isSaving = false)),
          )
          .subscribe({
            next: () => {
              this.changed = true;
              this.reloadColumns();
            },
          });
      });
  }

  private reloadColumns(): void {
    if (!this.funnelId) return;

    this.boardService
      .getColumns({
        config: {
          funnel_id: this.funnelId,
          is_view_success: true,
          is_view_failed: true,
        },
        filters: [],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((columns) => (this.stageColumns = this.copyColumns(columns ?? [])));
  }

  private copyColumns(columns: BoardColumn[]): BoardColumn[] {
    return columns.map((column) => ({
      ...column,
      color: { ...column.color },
    }));
  }

  isSystemColumn(column: BoardColumn): boolean {
    return column.type === 'SUCCESS' || column.type === 'FAILED';
  }

  isLastSystemColumn(column: BoardColumn): boolean {
    return this.isSystemColumn(column) && this.countSystemColumns(column.type) <= 1;
  }

  private countSystemColumns(type: BoardColumnType): number {
    return this.stageColumns.filter((column) => column.type === type).length;
  }

  private hasSuccessColumn(): boolean {
    return this.countSystemColumns('SUCCESS') > 0;
  }

  private getExcludedColumnTypesForCreate(): BoardColumnType[] {
    return this.hasSuccessColumn() ? ['SUCCESS'] : [];
  }

  private canApplyColumnOrder(previousColumns: BoardColumn[], nextColumns: BoardColumn[]): boolean {
    return previousColumns.every((column, index) => column.type === nextColumns[index]?.type);
  }

  private getNewColumnPosition(type: BoardColumnType): number {
    const sortedColumns = [...this.stageColumns].sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));

    if (type === 'FAILED') {
      const firstSuccessColumn = sortedColumns.find((column) => column.type === 'SUCCESS');
      if (firstSuccessColumn) {
        const beforeSuccessColumns = sortedColumns.filter(
          (column) => Number(column.position ?? 0) < Number(firstSuccessColumn.position ?? 0),
        );
        return this.getPositionBetween(beforeSuccessColumns.at(-1)?.position, firstSuccessColumn.position);
      }
    }

    return Math.max(0, ...sortedColumns.map((item) => Number(item.position) || 0)) + 1;
  }

  private getPositionBetween(before: number | null | undefined, after: number | null | undefined): number {
    const beforePosition = Number(before ?? 0);
    const afterPosition = Number(after ?? beforePosition + 2);

    return afterPosition > beforePosition ? beforePosition + (afterPosition - beforePosition) / 2 : beforePosition + 1;
  }
}
